import { useEffect, useRef, useState } from 'react'
import type { DiagnosisDefault } from './types'

type Params = {
  /** Current value of the diagnosis field (newline-joined `"CODE display"` lines). */
  currentValue: string
  /** Patient's active-condition ICD-10 codes, from `useAutoPopulate`. */
  defaults: DiagnosisDefault[]
  /** Called once with the seeded value when the field is empty and defaults exist. */
  onSeed: (value: string) => void
}

/**
 * Pre-populates an order form's ICD-10 diagnosis field from the patient's active
 * conditions. Seeds exactly once — the first time defaults are available — and only
 * when the field is still empty, so it never overwrites a resumed draft or codes the
 * provider has already typed. Returns whether it performed the auto-fill.
 */
export function useSeedDiagnosisCodes({ currentValue, defaults, onSeed }: Params): boolean {
  const attemptedRef = useRef(false)
  const [autoFilled, setAutoFilled] = useState(false)

  // Read the latest value/callback without re-triggering the seed effect.
  const currentValueRef = useRef(currentValue)
  currentValueRef.current = currentValue
  const onSeedRef = useRef(onSeed)
  onSeedRef.current = onSeed

  useEffect(() => {
    if (attemptedRef.current) {
      return
    }
    // Defaults load asynchronously — wait until they arrive before deciding.
    if (defaults.length === 0) {
      return
    }
    attemptedRef.current = true

    if (currentValueRef.current.trim()) {
      return
    }

    onSeedRef.current(defaults.map((d) => `${d.code} ${d.display}`).join('\n'))
    setAutoFilled(true)
  }, [defaults])

  return autoFilled
}
